import { FlexColumn, FlexRow, StyledButton, useLoading } from '@gorila-shared-ui/components';
import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES } from '../../../constants/app';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import { createJobType, updateJobType } from '../../../services/jobTypesService';
import { editJobTypeErrorState, editJobTypeState, editJobTypeTouchedState } from '../../../storage/jobTypesStates';
import { JobType } from '../../../types/jobs';
import { JobTypeFormSteps } from './JobTypeFormSteps';

type Props = {
  jobTypeId?: string;
  jobType?: JobType;
  onCancel?: () => void;
  onSubmitFinished?: () => void;
};
export function JobTypeForm({ jobType, jobTypeId, onCancel, onSubmitFinished }: Readonly<Props>) {
  const editMode = jobTypeId !== undefined && jobType !== undefined;
  const { css, theme } = useStyles();
  const { loading, startLoading, stopLoading } = useLoading();
  const [form, setForm] = useRecoilState(editJobTypeState);
  const formHasError = useRecoilValue(editJobTypeErrorState);
  const resetEditJobTypeState = useResetRecoilState(editJobTypeState);
  const resetEditJobTypeTouchedState = useResetRecoilState(editJobTypeTouchedState);
  const { showFailFeedback, showPositiveFeedback } = useFeedback();

  const totalSteps = 2;
  const [currentStep, setCurrentStep] = useState(0);
  const isLastStep = currentStep + 1 === totalSteps;

  const canContinue = !formHasError;

  const canSubmit = form.technicianSkills?.length !== 0 && !formHasError;

  useEffect(() => {
    return () => {
      clearStates();
    };
  }, []);

  useEffect(() => {
    if (!jobType) return;
    setForm((prev) => ({
      ...prev,
      ...jobType,
      tenants: jobType.tenants.map((tenant) => tenant._id),
    }));
  }, [jobType]);

  const clearStates = () => {
    resetEditJobTypeState();
    resetEditJobTypeTouchedState();
  };

  const onSubmit = async () => {
    const onSuccess = (successMessage: string) => {
      showPositiveFeedback(successMessage);
      clearStates();
      onSubmitFinished && onSubmitFinished();
    };
    startLoading();
    if (editMode) {
      const { error } = await updateJobType(jobTypeId, form);
      if (!error) {
        onSuccess(FEEDBACK.edited(FEEDBACK_PREFIXES.jobType, form.name));
      } else {
        showFailFeedback(error);
      }
    } else {
      const { error } = await createJobType(form);
      if (!error) {
        onSuccess(FEEDBACK.created(FEEDBACK_PREFIXES.jobType));
      } else {
        showFailFeedback(error);
      }
    }
    stopLoading();
  };

  return (
    <FlexColumn
      classNames={css({
        justifyContent: 'space-between',
        overflow: 'hidden',
        minWidth: '525px',
      })}
    >
      <FlexColumn
        classNames={css({
          overflow: 'auto',
          height: '100%',
        })}
      >
        <JobTypeFormSteps
          jobType={jobType}
          currentStep={currentStep}
        />
      </FlexColumn>
      <FlexRow classNames={`${css({ alignItems: 'center', justifyContent: 'flex-end' })}`}>
        <FlexRow gap={theme.sizing.scale300}>
          {currentStep > 0 && (
            <StyledButton
              kind="tertiary"
              onClick={() => setCurrentStep((prev) => prev - 1)}
            >
              Regresar
            </StyledButton>
          )}
          {jobType?._id && (
            <StyledButton
              kind="tertiary"
              onClick={onCancel}
            >
              Cancelar
            </StyledButton>
          )}
          {!isLastStep && (
            <StyledButton
              onClick={() => setCurrentStep((prev) => prev + 1)}
              disabled={!canContinue}
            >
              Siguiente
            </StyledButton>
          )}
          {isLastStep && (
            <StyledButton
              onClick={onSubmit}
              isLoading={loading}
              disabled={!canSubmit}
            >
              Guardar
            </StyledButton>
          )}
        </FlexRow>
      </FlexRow>
    </FlexColumn>
  );
}
